export interface IsoWeekInfo {
  isoYear: number
  isoWeek: number
}

const pad2 = (n: number): string => String(n).padStart(2, "0")

/**
 * ISO 8601 週番号を算出する（月の概念に依存しない、年をまたいでも一意な週番号）。
 * 年末年始の数日は isoYear が calendar year と異なる場合がある（ISO 8601 の仕様通り）。
 */
export const getIsoWeekInfo = (date: Date): IsoWeekInfo => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7 // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum) // 週の木曜日に揃える
  const isoYear = d.getUTCFullYear()
  const yearStart = new Date(Date.UTC(isoYear, 0, 1))
  const isoWeek = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  return { isoYear, isoWeek }
}

/** 指定した ISO 週年の最終週番号（52 または 53）。12/28 は必ずその年の最終週に含まれる。 */
export const getIsoWeeksInYear = (isoYear: number): number => {
  return getIsoWeekInfo(new Date(isoYear, 11, 28)).isoWeek
}

/** 指定した ISO 週（月曜始まり）の開始日・終了日を YYYY-MM-DD 形式で返す。 */
export const getIsoWeekDateRange = (isoYear: number, isoWeek: number): { start: string; end: string } => {
  const simple = new Date(Date.UTC(isoYear, 0, 1 + (isoWeek - 1) * 7))
  const dayOfWeek = simple.getUTCDay() || 7
  const startUtc = new Date(simple)
  startUtc.setUTCDate(simple.getUTCDate() - dayOfWeek + 1)
  const endUtc = new Date(startUtc)
  endUtc.setUTCDate(startUtc.getUTCDate() + 6)
  const fmt = (d: Date) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`
  return { start: fmt(startUtc), end: fmt(endUtc) }
}

export const buildWeekId = (isoYear: number, isoWeek: number): string => `${isoYear}-W${pad2(isoWeek)}`

const WEEK_ID_PATTERN = /^(\d{4})-W(\d{2})$/

export const parseWeekId = (weekId: string): IsoWeekInfo | null => {
  const match = WEEK_ID_PATTERN.exec(weekId)
  if (!match) return null
  const isoWeek = Number(match[2])
  if (isoWeek < 1 || isoWeek > 53) return null
  return { isoYear: Number(match[1]), isoWeek }
}
